import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { NeedTenant } from "../hooks/useData";
import { GlassCard, EmptyState, SectionTitle } from "../components/common";
import { Button } from "../components/ui/button";
import client from "../lib/api";
import { Sparkles, Search, Loader2, Cpu, Users, Workflow, ShieldAlert, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";

const DIM_ICON = { Process: Workflow, Resource: Users, System: Cpu, Compliance: ShieldAlert, Volume: BarChart3 };

function Inner() {
  const { datasetId, currentDataset } = useApp();
  const [insight, setInsight] = useState("");
  const [loadingIns, setLoadingIns] = useState(false);
  const [rca, setRca] = useState(null);
  const [loadingRca, setLoadingRca] = useState(false);

  if (!datasetId) return <EmptyState icon={Sparkles} title="No dataset selected" />;

  const genInsight = async () => {
    setLoadingIns(true);
    try { const { data } = await client.post("/ai/insight", { dataset_id: datasetId }); setInsight(data.insight); }
    catch { toast.error("Could not generate insight"); } finally { setLoadingIns(false); }
  };
  const genRca = async () => {
    setLoadingRca(true);
    try { const { data } = await client.post("/ai/root-cause", { dataset_id: datasetId }); setRca(data); }
    catch { toast.error("Could not run root-cause analysis"); } finally { setLoadingRca(false); }
  };

  return (
    <div className="space-y-5 pm-fade-up">
      <SectionTitle eyebrow="AI-Native Intelligence" title="Insights & Root-Cause Analysis" sub={currentDataset?.name} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-xl pm-gradient flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div><h3 className="font-bold text-slate-900">Process narrative</h3></div>
            <Button onClick={genInsight} disabled={loadingIns} data-testid="gen-narrative" size="sm" className="rounded-xl pm-gradient text-white border-0">{loadingIns ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}</Button>
          </div>
          {insight ? <Markdown>{insight}</Markdown>
            : <p className="text-sm text-slate-400 py-8 text-center">Generate an executive narrative interpreting the mining results.</p>}
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center"><Search className="w-4 h-4 text-white" /></div><h3 className="font-bold text-slate-900">Root-cause analysis</h3></div>
            <Button onClick={genRca} disabled={loadingRca} data-testid="gen-rca" size="sm" className="rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white border-0">{loadingRca ? <Loader2 className="w-4 h-4 animate-spin" /> : "Analyse"}</Button>
          </div>
          {rca ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-700 leading-relaxed">{rca.summary}</p>
              <div className="space-y-2.5">
                {(rca.root_causes || []).map((c, i) => {
                  const Icon = DIM_ICON[c.dimension] || Workflow;
                  return (
                    <div key={i} className="p-3 rounded-xl bg-white/60 border border-white/60" data-testid={`rca-cause-${i}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Icon className="w-4 h-4 text-amber-600" />{c.cause}</span>
                        <span className="text-xs font-bold text-amber-600">{c.likelihood}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-1.5"><div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: `${c.likelihood}%` }} /></div>
                      <p className="text-xs text-slate-500">{c.evidence}</p>
                    </div>
                  );
                })}
              </div>
              {rca.recommended_action && <div className="p-3 rounded-xl bg-cyan-50 border border-cyan-200 text-sm text-cyan-800"><span className="font-semibold">Recommended action: </span>{rca.recommended_action}</div>}
            </div>
          ) : <p className="text-sm text-slate-400 py-8 text-center">Run multi-dimensional causal attribution across process, resource, system and compliance factors.</p>}
        </GlassCard>
      </div>
    </div>
  );
}

export default function Insights() { return <NeedTenant><Inner /></NeedTenant>; }
